import React from 'react';
import { Link as RouterLink, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Box, Container, Typography, Button, TextField } from '@mui/material';
import PublicNavbar from '../components/public/PublicNavbar';
import Navbar from '../components/Navbar';
import authService from '../services/auth';
import { BEGINNER_TERMS } from '../content/beginnerTerms';
import { LEARN_CATEGORIES } from '../content/learnCenter';
import { rentalUnderwritingEnabled } from '../config/featureFlags';
import { focusRing } from '../components/public/landingStyles';

const LearnPage: React.FC = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const topic = slug || params.get('topic') || 'all';
  const [query, setQuery] = React.useState('');
  const [copyState, setCopyState] = React.useState('');
  const needle = query.trim().toLowerCase();

  const visible = LEARN_CATEGORIES.filter((category) => topic === 'all' || category.id === topic)
    .map((category) => {
      const terms = category.termIds
        .map((id) => BEGINNER_TERMS[id])
        .filter((item) => {
          if (!needle) return true;
          const haystack = `${category.title} ${category.intro} ${item.label} ${item.short} ${item.detail}`.toLowerCase();
          return haystack.includes(needle);
        });
      const introMatches = !needle || `${category.title} ${category.intro}`.toLowerCase().includes(needle);
      if (!introMatches && terms.length === 0) return null;
      return { category, terms: needle && !introMatches ? terms : needle ? terms : category.termIds.map((id) => BEGINNER_TERMS[id]) };
    })
    .filter((row): row is NonNullable<typeof row> => row !== null);

  const selectTopic = (id: string) => {
    navigate(id === 'all' ? '/learn' : `/learn/${id}`);
  };

  const copyLink = async (id: string) => {
    const url = `${window.location.origin}/learn/${id}`;
    try {
      if (!navigator.clipboard?.writeText) throw new Error('clipboard');
      await navigator.clipboard.writeText(url);
      setCopyState(`Copied the link for ${id}.`);
    } catch {
      setCopyState(url);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {authService.getToken() ? <Navbar /> : <PublicNavbar />}
      <Container id="main" component="main" tabIndex={-1} maxWidth="md" sx={{ py: 4, minWidth: 0 }}>
        <Typography variant="h3" component="h1" sx={{ fontWeight: 800, mb: 1, overflowWrap: 'break-word' }}>
          Learning Center
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          {rentalUnderwritingEnabled()
            ? 'Explanations DealSkout uses while you analyze a flip, a buy-and-hold, or a BRRRR. Rental results are a saved model, not a lender approval or a recommendation to purchase.'
            : 'Explanations DealSkout already uses while you analyze a flip or resale. This is not rental underwriting, and it is not a recommendation to purchase.'}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          These definitions are DealSkout&apos;s glossary. They describe the product, not a live listing, so they do
          not have a last-checked date. A plain-text copy is on the{' '}
          <Box component="a" href="/guides/index.html" sx={{ fontWeight: 800, color: 'text.primary' }}>
            text version
          </Box>
          .
        </Typography>
        <TextField
          fullWidth
          label="Search explanations"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          sx={{ mb: 2 }}
        />
        <Box role="group" aria-label="Learning categories" sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
          <Button
            size="small"
            variant={topic === 'all' ? 'contained' : 'outlined'}
            aria-pressed={topic === 'all'}
            onClick={() => selectTopic('all')}
          >
            All topics
          </Button>
          {LEARN_CATEGORIES.map((category) => (
            <Button
              key={category.id}
              size="small"
              variant={topic === category.id ? 'contained' : 'outlined'}
              aria-pressed={topic === category.id}
              onClick={() => selectTopic(category.id)}
              sx={{ minWidth: 0 }}
            >
              {category.title}
            </Button>
          ))}
        </Box>
        {copyState && (
          <Typography variant="body2" role="status" sx={{ mb: 2, overflowWrap: 'break-word', wordBreak: 'normal' }}>
            {copyState}
          </Typography>
        )}
        <Box sx={{ display: 'grid', gap: 4 }}>
          {visible.length === 0 && (
            <Typography>No matching explanations. Try another word, or choose All topics.</Typography>
          )}
          {visible.map(({ category, terms }) => (
            <Box key={category.id} id={category.id} component="section" aria-labelledby={`learn-${category.id}`}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 1 }}>
                <Typography id={`learn-${category.id}`} variant="h5" component="h2" sx={{ fontWeight: 800, minWidth: 0 }}>
                  {category.title}
                </Typography>
                <Button size="small" variant="text" onClick={() => copyLink(category.id)} sx={focusRing}>
                  Copy link
                </Button>
              </Box>
              <Typography sx={{ mb: 2 }}>{category.intro}</Typography>
              <Box sx={{ display: 'grid', gap: 1.5 }}>
                {terms.map((item) => (
                  <Box
                    key={`${category.id}-${item.id}`}
                    id={`${category.id}-${item.id}`}
                    sx={{
                      p: 2,
                      minWidth: 0,
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      bgcolor: 'background.paper',
                    }}
                  >
                    <Typography variant="subtitle1" sx={{ fontWeight: 800, overflowWrap: 'break-word' }}>
                      {item.label}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      {item.short}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      {item.detail}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          ))}
        </Box>
        <Button component={RouterLink} to="/signup" variant="contained" sx={{ mt: 4, ...focusRing }}>
          Start free
        </Button>
      </Container>
    </Box>
  );
};

export default LearnPage;
